/**
 * ConcernCode represents a specific food data concern
 */
export interface ConcernCode {
    code: number;
    name: string;
    description: string;
}

/**
 * ConcernCodes contains all possible food concern codes
 */
export const ConcernCodes = {
    // Nutrient relationship concerns (1000-1099)
    NetCarbsAmountIssue: {
        code: 1000,
        name: "NET_CARBS_AMOUNT_ISSUE",
        description:
            "Net carbs calculation doesn't match ingredient composition",
    },
    SuspectCalories: {
        code: 1001,
        name: "SUSPECT_CALORIES",
        description:
            "Calorie count appears inconsistent with macro nutrient values",
    },
    MissingEssentialNutrients: {
        code: 1002,
        name: "MISSING_ESSENTIAL_NUTRIENTS",
        description:
            "Missing critical nutrients such as calories or macronutrients",
    },
    FatAmountIssue: {
        code: 1003,
        name: "FAT_AMOUNT_ISSUE",
        description: "Fat subcomponents don't sum correctly",
    },
    SugarAmountIssue: {
        code: 1004,
        name: "SUGAR_AMOUNT_ISSUE",
        description: "Added sugars exceed total sugars",
    },

    // Portion/serving concerns (1100-1199)
    InvalidServingSize: {
        code: 1100,
        name: "INVALID_SERVING_SIZE",
        description: "Serving size is missing or has invalid weight",
    },
    DuplicatePortions: {
        code: 1101,
        name: "DUPLICATE_PORTIONS",
        description: "Multiple portion entries with identical names detected",
    },
    InconsistentPortions: {
        code: 1102,
        name: "INCONSISTENT_PORTIONS",
        description:
            "Portion measurements don't align with standard conversion ratios",
    },
    InvalidPortionName: {
        code: 1103,
        name: "INVALID_PORTION_NAME",
        description: "Portion name is missing or invalid",
    },
    PortionWeightTooHigh: {
        code: 1104,
        name: "PORTION_WEIGHT_TOO_HIGH",
        description: "Portion weight exceeds reasonable limits",
    },
    InvalidOunceWeight: {
        code: 1105,
        name: "INVALID_OUNCE_WEIGHT",
        description: "Ounce portion weight outside valid range",
    },
    GramsInPortionName: {
        code: 1106,
        name: "GRAMS_IN_PORTION_NAME",
        description:
            "Portion name contains unit that should be in weight field",
    },
    DuplicatePortionName: {
        code: 1107,
        name: "DUPLICATE_PORTION_NAME",
        description:
            "Multiple portions have the same name when case is ignored",
    },
    BadServingConfig: {
        code: 1108,
        name: "BAD_SERVING_CONFIG",
        description: "Portion with 'serving' in name has default weight of 1",
    },

    // Data quality concerns (1200-1299)
    InvalidNutrientRanges: {
        code: 1200,
        name: "INVALID_NUTRIENT_RANGES",
        description:
            "One or more nutrient values fall outside reasonable ranges",
    },
    SuspectUnitValues: {
        code: 1201,
        name: "SUSPECT_UNIT_VALUES",
        description:
            "Units appear to be incorrectly scaled or use wrong magnitude",
    },
    LowQualityScore: {
        code: 1202,
        name: "LOW_QUALITY_SCORE",
        description: "Item has been flagged as having low data quality",
    },
    DuplicateNutrients: {
        code: 1203,
        name: "DUPLICATE_NUTRIENTS",
        description: "Multiple entries for the same nutrient",
    },
    MissingFoodName: {
        code: 1204,
        name: "MISSING_FOOD_NAME",
        description: "Food name is missing",
    },
    InvalidWeightUnit: {
        code: 1205,
        name: "INVALID_WEIGHT_UNIT",
        description: "Weight unit is not g or ml",
    },

    // Branded item concerns (1300-1399)
    MissingIngredients: {
        code: 1300,
        name: "MISSING_INGREDIENTS",
        description: "Branded item is missing ingredient list",
    },
    InvalidIngredientFormat: {
        code: 1301,
        name: "INVALID_INGREDIENT_FORMAT",
        description: "Ingredient list is not properly formatted",
    },
    MissingBrandInfo: {
        code: 1302,
        name: "MISSING_BRAND_INFO",
        description:
            "Product has a code but missing brand name or owner information",
    },

    // Tag consistency concerns (1400-1499)
    VeganTagInconsistency: {
        code: 1400,
        name: "VEGAN_TAG_INCONSISTENCY",
        description: "Vegan tag conflicts with dairy/egg content",
    },
    AlcoholTagInconsistency: {
        code: 1401,
        name: "ALCOHOL_TAG_INCONSISTENCY",
        description: "Alcohol tag/nutrient mismatch",
    },
    CaffeineTagInconsistency: {
        code: 1402,
        name: "CAFFEINE_TAG_INCONSISTENCY",
        description: "Caffeine tag/nutrient mismatch",
    },
} as const;

/**
 * Create a lookup map for fast concern code resolution
 */
export const ConcernCodeLookup: Record<number, ConcernCode> = Object.values(
    ConcernCodes
).reduce((lookup, concern) => {
    lookup[concern.code] = concern;
    return lookup;
}, {} as Record<number, ConcernCode>);

/**
 * Get concern code information by code number
 */
export function getConcernCode(code: number): ConcernCode | undefined {
    return ConcernCodeLookup[code];
}

/**
 * Get concern severity level based on code range
 */
export function getConcernSeverity(code: number): "high" | "medium" | "low" {
    if (code >= 1000 && code < 1100) return "high"; // Nutrient issues
    if (code >= 1100 && code < 1200) return "medium"; // Portion issues
    if (code >= 1200 && code < 1300) return "medium"; // Data quality
    if (code >= 1300 && code < 1400) return "low"; // Branded items
    if (code >= 1400 && code < 1500) return "medium"; // Tag consistency
    return "low";
}
